import React from 'react';
import PropTypes from 'prop-types';
import css from '@src/styles/components/form/select.scss';
import AdaptiveTrait from '../../AdaptiveTrait';
import Api from '../../Api';

const config = {

    options: {
        queryName: 'q',
        valueName: 'id',
        textName: 'name',
        debounce: 850,
    },

    modification: {
        leftToRight: css['select--left-to-right'],
        search: css['select--search'],
    },

    mode: {
        active: css['active'],
        error: css['has-error'],
        loading: css['loading'],
        inactive: null,
    },

    classes: {
        option: css['select__option'],
        checkedOption: css['select__option--checked'],
        selectedOption: css['select__option--selected'],
        icon: css['select__icon'],
    },
};

export default class Select extends React.Component {

    constructor(props) {
        
        super(props);

        this.mounted = null;

        this.nodes = {
            select: null,
            current: null,
        };

        this.state = {
            items: this.generateDefaultItems(),
        };

        this.onWindowResize = this.onWindowResize.bind(this);
        this.onDocumentMouseDown = this.onDocumentMouseDown.bind(this);
        this.onDocumentClick = this.onDocumentClick.bind(this);
    }
    
    static get propTypes() {

        return {
            className: PropTypes.string,
            url: PropTypes.string,
            queryName: PropTypes.string,
            onChange: PropTypes.func,
            children: PropTypes.any,
            value: PropTypes.any,
            name: PropTypes.string,
        };
    }

    //------------------------------------------------------------------------------------------------------------------
    // INIT
    //------------------------------------------------------------------------------------------------------------------

    componentDidMount() {

        if (this.mounted === true)
            return;

        AdaptiveTrait.addResizeEvent(this.onWindowResize);
        document.addEventListener('mousedown', this.onDocumentMouseDown);
        document.addEventListener('click', this.onDocumentClick);
        
        if (this.isSearchable)
            this.mountSearchable();

        this.nodes.native.addEventListener('change', (e) => this.onSelectChange(e));

        this.mounted = true;
    }

    componentWillUnmount() {

        if (this.mounted === false)
            return;
        
        AdaptiveTrait.removeResizeEvent(this.onWindowResize);
        document.removeEventListener('mousedown', this.onDocumentMouseDown);
        document.removeEventListener('click', this.onDocumentClick);

        this.mounted = false;
    }

    componentDidUpdate() {

        if (this.isSearchable && this.openRequired) {
            
            this.openRequired = false;
            this.open();
        }
    }

    mountSearchable() {

        let timeout = null;
        this.nodes.current.addEventListener('input', () => {

            this.setMode(config.mode.loading);
            if (timeout != null)
                clearTimeout(timeout);
            timeout = setTimeout(() => this.search(), config.options.debounce);
        });

        this.nodes.current.addEventListener('keyup', (e) => this.onSelectKeyUp(e));
    }

    //------------------------------------------------------------------------------------------------------------------
    // RENDER
    //------------------------------------------------------------------------------------------------------------------

    render() {

        const classes = [css['select']];

        if (this.props.url != undefined)
            classes.push(config.modification.search);

        if (this.props.className != undefined)
            classes.push(this.props.className);

        return (
            <div
                className={ classes.join(' ') }
                ref={ (node) => this.createRef(node, 'select') }
                { ...this.unusedProps }>

                <select
                    ref={ (node) => this.createRef(node, 'native') }
                    name={ this.props.name }
                    defaultValue={ this.defaultValue }>

                    { this.renderNativeOptions() }

                </select>

                <div className={ css['select__wrapper'] }>
                    { this.renderCurrent() }
                    { this.renderStyledOptions() }
                </div>

            </div>
        );
    }

    /** private */ renderCurrent() {

        const
            CurrentTag = this.props.url != undefined
                ? 'input'
                : 'div',
            selected = this.selectedOption;
        return <>
            <CurrentTag
                className={ css['select__current'] }
                ref={ (node) => this.createRef(node, 'current') }
                value={ selected?.value }>
                { selected?.content }
            </CurrentTag>
            <div className={ config.classes.icon }></div>
        </>;
    }
    
    /** private */ renderStyledOptions() {

        const
            defaultValue = this.defaultValue,
            
            items = this.state.items.map((item) => {

                const classes = [config.classes.option];
                if (item.value == defaultValue)
                    classes.push(config.classes.checkedOption);
            
                return (
                    <div key={ `styled${ item.value }` } className={ classes.join(' ') } data-value={ item.value }>
                        { item.content }
                    </div>
                );
            });

        return (
            <div className={ css['select__options'] } ref={ (node) => this.createRef(node, 'options') }>
                { items }
            </div>
        );
    }
    
    /** private */ renderNativeOptions() {

        return this.state.items.map((item) => {

            return <option key={ `option${ item.value }` } value={ item.value }>{ item.text }</option>;
        });
    }

    //------------------------------------------------------------------------------------------------------------------
    // ACTIONS
    //------------------------------------------------------------------------------------------------------------------

    open() {

        if (this.state.items.length > 0) {

            // selector has options

            this.setMode(config.mode.active);

            const classes = this.nodes.select.classList;
            if (!classes.contains(config.modification.leftToRight)) {

                if (this.nodes.options.getBoundingClientRect().x < 0)
                    classes.add(config.modification.leftToRight);
                // fix out of bounds on the left
            }
        }
    }

    close() {

        this.nodes.select.classList.remove(config.modification.leftToRight);
        this.setMode(config.mode.inactive);
    }

    clear() {

        this.nodes.current.innerText = '';
        this.nodes.current.value = null;
        this.setState({ items: [] })
    }

    search() {

        const queryName = this.props.queryName ?? config.options.queryName;

        const url = new URL(this.props.url);
        url.searchParams.append(queryName, this.nodes.current.value);

        Api.fetch(url.toString())
            .then((response) => {

                if (response.length > 0) {

                    const items = [];

                    response.forEach((item) => {
    
                        items.push({
                            content: item[config.options.textName],
                            text: item[config.options.textName],
                            value: item[config.options.valueName],
                        });
                    });
                    
                    this.openRequired = true;
                    this.setState({ items });
                }
                else {

                    this.setMode(config.mode.inactive);
                }
            })
            .catch(() => {
                
                this.setMode(config.mode.error);
            });
    }

    setMode(mode) {

        const classes = this.nodes.select.classList;

        Object.values(config.mode).forEach((className) => {

            if (className != mode)
                classes.remove(className);
        });

        if (mode != null)
            classes.add(mode);
    }

    shiftOption(direction) {

        if (!this.isOpen)
            this.open();

        if (this.isOpen) {
            
            let newSelectedOption = null;

            const
                options = this.nodes.options,
                selectedOption = options.querySelector(`.${config.classes.selectedOption}`)
                    ?? options.querySelector(`.${config.classes.checkedOption}`);

            if (selectedOption) {

                selectedOption.classList.remove(config.classes.selectedOption);
                newSelectedOption = direction > 0 ? selectedOption.nextSibling : selectedOption.previousSibling;
            }

            if (newSelectedOption == null)
                newSelectedOption = direction > 0 ? options.firstChild : options.lastChild;

            if (newSelectedOption != null) {
                
                newSelectedOption.classList.add(config.classes.selectedOption);
                newSelectedOption.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                });
            }
        }
    }

    setActiveBySelectedOption() {

        const selectedOption = this.nodes.options.querySelector(`.${config.classes.selectedOption}`);
        if (selectedOption) {

            selectedOption.classList.remove(config.classes.selectedOption);
            this.setActiveByOption(selectedOption, true);
        }
    }

    setActiveByValue(value) {

        const option = this.nodes.options.querySelector(`[data-value="${value}"]`);
        this.setActiveByOption(option, true);
    }

    setActiveByOption(option, forced = false) {

        const value = option?.dataset.value;

        this.nodes.current.tagName.toUpperCase() == 'INPUT'
            ? this.nodes.current.value = option?.innerText
            : this.nodes.current.innerHTML = option?.innerHTML;

        if (forced || this.nodes.native.value !== value) {

            this.nodes.native.value = value;
            this.nodes.native.dispatchEvent(new Event('change'));
    
            this.nodes.options
                .getElementsByClassName(config.classes.checkedOption)[0]
                ?.classList.remove(config.classes.checkedOption);

            if (option)
                option.classList.add(config.classes.checkedOption);

            if (this.props.onChange != undefined) {

                this.props.onChange(new CustomEvent('change', {
                    detail: (value ? this.getItemByValue(value) : null),
                }));
            }
        }
    }

    //------------------------------------------------------------------------------------------------------------------
    // EVENTS
    //------------------------------------------------------------------------------------------------------------------

    onDocumentMouseDown() {

        this.focusedElement = document.activeElement;
    }

    onDocumentClick(e) {

        if (e.button !== /* LEFT BUTTON CLICK */ 0)
            return;

        const { target } = e;

        if (!this.isInteractionAvailable) {

            if (target == this.nodes.native)
                this.nodes.current.focus();
            // fix by native select

            return;
        }

        if (this.isSearchable && target.classList.contains(config.classes.icon))
            this.clear();
        // click to clear button

        if (!this.isOpen) {

            if (target == this.nodes.select || this.nodes.select.contains(target)) {

                this.open();

                if (!target.classList.contains(config.classes.icon) || !this.isFocused)
                    this.nodes.current.focus();
            }
        }
        else {

            // opened

            this.close();

            if (this.nodes.select.contains(target)) {

                const parentOption = target.closest(`.${config.classes.option}`);
                if (parentOption)
                    return this.setActiveByOption(parentOption);

                if (target.classList.contains(config.classes.option))
                    return this.setActiveByOption(target);
            }
        }
    }

    onWindowResize() {

        if (this.isOpen)
            this.close();
    }

    onSelectChange(e) {

        if (e.isTrusted)
            this.setActiveByValue(this.nodes.native.value);
    }

    onSelectKeyUp(e) {

        if (!this.isFocused)
            return false;

        switch (e.code) {

            case 'Escape':
                e.preventDefault();
                this.close();
                break;

            case 'ArrowDown':
                e.preventDefault();
                this.shiftOption(+1);
                break;

            case 'ArrowUp':
                e.preventDefault();
                this.shiftOption(-1);
                break;

            case 'Enter':
                e.preventDefault();
                this.close();
                this.setActiveBySelectedOption();
                break;
        }
    }

    //------------------------------------------------------------------------------------------------------------------
    // HELPERS
    //------------------------------------------------------------------------------------------------------------------

    getItemByValue(value) {

        let foundedItem = null;

        this.state.items.forEach((item) => {

            if (item.value == value)
                foundedItem = item;
        });

        return foundedItem;
    }

    /** private */ createRef(node, name) {

        this.nodes[name] = node;
    }

    /** private */ getContentFromProp(prop) {
        
        if (typeof(prop) == 'string')
            return <div>{ prop }</div>;
            
        if (typeof(prop) == 'object' && prop.length > 0) {
            
            return Object
                .values(prop)
                .map((subProp) => this.getContentFromProp(subProp));
        }

        return prop;
    }

    /** private */ getTextFromProp(prop) {
        
        if (!prop)
            return '';

        let text = '';

        const contentType = typeof(prop);
        switch (contentType) {

            case 'string':
                text = new String(prop);
                break;

            case 'array':
                prop.forEach((subProp) => {

                    text+=' ';
                    text+= this.getTextFromProp(subProp);
                });
                text = text.trim();
                break;

            case 'object':
                if (prop.length) {
                    
                    // fix for array object

                    Object.values(prop).forEach((subProp) => {

                        text+=' ';
                        text+= this.getTextFromProp(subProp);
                    });
                    text = text.trim();
                }
                break;
        }

        return text;
    }

    //------------------------------------------------------------------------------------------------------------------
    // GETTERS & SETTERS
    //------------------------------------------------------------------------------------------------------------------

    get isSearchable() {

        return this.nodes.select?.classList.contains(config.modification.search);
    }

    get isFocused() {

        if (this.nodes.current === this.focusedElement)
            return true;

        if (this.nodes.current === document.activeElement)
            return true;

        return false;
    }

    get isInteractionAvailable() {

        const classes = this.nodes.select.classList;

        return  !classes.contains(config.mode.error) &&
                !classes.contains(config.mode.loading);
    }

    get isOpen() {

        return this.nodes.select?.classList.contains(config.mode.active);
    }

    /** private */ get unusedProps() {
        
        const usedKeys = Object.keys(Select.propTypes);
        return Object.fromEntries(Object.entries(this.props).map(([key, value]) => {
            
            if (!usedKeys.includes(key))
                return [key, value];

        }).filter((value) => value !== undefined));
    }

    /** private */ generateDefaultItems() {

        return Array
            .from(this.props.children ?? [])
            .map((el, index) => {

                return {
                    content: this.getContentFromProp(el.props.children),
                    text: this.getTextFromProp(el.props.children),
                    value: el.props.value ?? index,
                };
            });
    }

    /** private */ get defaultValue() {

        if (this.props.value != undefined)
            return this.props.value;

        let defaultValue = null;
        Array
            .from(this.props.children ?? [])
            .forEach((el, index) => {

            if (el.props?.selected)
                defaultValue = el.props.value ?? index;
        });

        return defaultValue;
    }

    /** private */ get selectedOption() {

        const defaultValue = this.defaultValue;

        let defaultOption = null;
        this.state.items.forEach((option) => {

            if (option.value == defaultValue)
                defaultOption = option;
        });

        return defaultOption
    }
}